import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Info, CheckCircle, Sparkles, Star, Moon, Flower2 } from 'lucide-react';
import { remedies } from '../../data/cyclePhases';

export function RemediesCard() {
  return (
    <div className="theme-card p-6 relative overflow-hidden">
      {/* Ambient Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-purple-400/5 to-background"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_var(--tw-gradient-stops))] from-pink-300/10 via-purple-300/5 to-transparent"></div>
      <div className="absolute inset-0 backdrop-blur-[100px]"></div>

      <div className="relative">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/20 blur-2xl animate-pulse"></div>
            <Heart className="w-8 h-8 text-primary relative z-10" />
          </div>
          <div>
            <h2 className="text-xl font-semibold bg-gradient-to-r from-primary via-purple-400 to-pink-400 bg-clip-text text-transparent">
              Healing Remedies
            </h2>
            <p className="text-sm text-primary/80">Gentle care for your comfort & wellbeing ✨</p>
          </div>
        </div>

        {/* Remedies Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {remedies.map((remedy, index) => (
            <motion.div
              key={remedy.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="relative group"
            >
              {/* Card Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-background to-background/90 rounded-xl backdrop-blur-xl border border-primary/10"></div>
              
              {/* Glowing Effects */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-all duration-700 rounded-xl"></div>
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/10 via-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 blur-xl transition-all duration-700 rounded-xl"></div>

              {/* Content */}
              <div className="relative p-5 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Flower2 className="w-5 h-5 text-primary animate-pulse" />
                      <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full"></div>
                    </div>
                    <h3 className="font-medium bg-gradient-to-r from-primary to-purple-400 bg-clip-text text-transparent">
                      {remedy.name}
                    </h3>
                  </div>
                  <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-success/10 text-success text-xs backdrop-blur-sm">
                    <Star className="w-3 h-3" />
                    <span>{remedy.effectiveness}</span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {remedy.description}
                </p>

                {/* Method */}
                <div className="bg-primary/5 rounded-lg p-3 backdrop-blur-sm">
                  <div className="flex items-center gap-2 text-sm text-primary/90">
                    <Moon className="w-4 h-4" />
                    <span>{remedy.method}</span>
                  </div>
                </div>

                {/* Warning */}
                <div className="flex items-center gap-2 text-xs text-muted-foreground bg-background/30 px-3 py-2 rounded-lg backdrop-blur-sm">
                  <Info className="w-3 h-3 text-warning" />
                  <span>{remedy.warning}</span>
                </div>
              </div>

              {/* Hover Animation */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-xl"></div>
            </motion.div>
          ))}
        </div>

        {/* Helpful Tips */}
        <div className="mt-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-purple-500/5 to-pink-500/5 rounded-xl"></div>
          <div className="relative p-4 backdrop-blur-sm rounded-xl border border-primary/10">
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="leading-relaxed">
                  These gentle remedies are designed to provide natural relief. Remember to:
                </p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Listen to your body's needs</li>
                  <li>Start with gentle approaches</li>
                  <li>Combine remedies mindfully</li>
                  <li>Consult healthcare providers when needed</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}